/* eslint-disable */

import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom"; // For accessing ":id" from URL

import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  increment,
  writeBatch,
  runTransaction,
  Timestamp,
  arrayUnion
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import firebaseApp from "../firebase"; // Adjust path as needed

import md5 from "md5";

// Number of items (offers) per page
const ITEMS_PER_PAGE = 5;

// Consistent offer ID extraction (handles offerId, offer_id, id - normalize to string for reliable lookup)
function getOfferId(o, fallbackIndex = null) {
  const raw = o?.offerId ?? o?.offer_id ?? o?.id;
  if (raw != null && raw !== "") return String(raw);
  if (fallbackIndex != null) return `idx-${id}-${fallbackIndex}`;
  return null;
}

const API_BASE_URL = process.env.NODE_ENV === "development"
  ? "http://127.0.0.1:5002/offers-5e23d/us-central1"
  : "https://us-central1-offers-5e23d.cloudfunctions.net";

async function creditOfferOwnersViaAPI(viewerUid, toCredit) {
  if (!toCredit?.length) return;
  try {
    const res = await fetch(`${API_BASE_URL}/creditOfferOwners`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ viewerUid, toCredit }),
    });
    const text = await res.text();
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      console.error("[CreditTransfer] Invalid JSON response:", text?.slice(0, 200));
      return;
    }
    if (!res.ok) {
      console.error("[CreditTransfer] API error:", res.status, data?.error || text);
      return;
    }
    if (!data.success) console.error("[CreditTransfer] API error:", data.error);
  } catch (err) {
    console.error("[CreditTransfer] Failed to credit offer owners:", err);
  }
}

export default function SchoolPage() {
  const { id } = useParams(); // React Router hook to get ":id"
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [limitHit, setLimitHit] = useState(false); // State for search limit

  // Auth and user-tier logic
  const [user, setUser] = useState(null);
  const [userTier, setUserTier] = useState(null);

  // Credit system state  
  const [userCredits, setUserCredits] = useState(0);
  const [creditsDeducted, setCreditsDeducted] = useState(0);
  const [viewedOfferIds, setViewedOfferIds] = useState({}); // { offerId: true } - offers user has paid to view
  const [totalCreditsUsedThisSession, setTotalCreditsUsedThisSession] = useState(0);
  const [hasInitializedFirstPage, setHasInitializedFirstPage] = useState(false);
  const isNavigatingRef = useRef(false); // Prevent concurrent page charges

  // SAI is a numeric or null value
  const [SAI, setSAI] = useState(null);

  // Expanded offer state for dropdown details
  const [expandedOffer, setExpandedOffer] = useState(null);

  // Firestore, Auth
  const db = getFirestore(firebaseApp);
  const auth = getAuth(firebaseApp);

  // We'll store the random seed (whether from Firestore or a temporary fallback)
  const [userSchoolSeed, setUserSchoolSeed] = useState(null);

  // -----------------------
  // 1) Handle Auth Changes
  // -----------------------
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        // Fetch user tier and credits
        const userRef = doc(db, "users", currentUser.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserTier(userData.access || "Free");
          setUserCredits(userData.searchCredit || 0);
        } else {
          setUserTier("Free");
          setUserCredits(0);
        }
      } else {
        setUser(null);
        setUserTier(null);
        setUserCredits(0);
      }
    });

    return () => unsubscribe();
  }, [auth, db]);

  // ----------------------------------
  // 2) Fetch School Data and Seed Logic
  // ----------------------------------
  useEffect(() => {
    async function fetchSchoolAndSeed() {
      try {
        setLoading(true);

        // Fetch the School Document
        const schoolDocRef = doc(db, "offerLetters", id);
        const schoolDocSnap = await getDoc(schoolDocRef);

        if (!schoolDocSnap.exists()) {
          console.error("No document found for the selected school.");
          setSchoolData(null);
          setLoading(false);
          return;
        }

        const schoolObj = schoolDocSnap.data();
        console.log(`[School Data] Fetched schoolDoc for ID = ${id}`, schoolObj);

        // If user is logged in, do the "myColleges" seed logic
        if (user) {
          console.log(`[Seed Logic] User is logged in: ${user.uid}`);
          const userRef = doc(db, "users", user.uid);
          const userDocSnap = await getDoc(userRef);

          if (!userDocSnap.exists()) {
            // If user doc doesn't exist, just show the data in locked form
            console.error("User document not found for the current user.");
            setSchoolData(schoolObj);
            setLoading(false);
            return;
          }

          const userData = userDocSnap.data() || {};
          const myColleges = userData.myColleges || [];
          const viewedOffers = userData.viewedOffers || {}; // { offerId: true } or legacy keys

          // Look for existing entry
          let existingEntry = myColleges.find(
            (item) => item.schoolId === id
          );

          // If no entry, generate a seed and push it into myColleges
          let seed = "";
          const batch = writeBatch(db); // Initialize a batch
          if (!existingEntry) {
            const currentSearches = userData?.searches || 0;
            const maxSearches = userData?.max_searches || 0;
            if (currentSearches >= maxSearches) {
              console.log(`[Search Limit] User ${user.uid} has reached the search limit of ${maxSearches}`);
              setLimitHit(true); // Update state to indicate search limit reached
              // Still create a seed for the user to maintain consistency, but don't track this search
              seed = Math.random().toString(36).substring(2, 15);
              const schoolName = schoolObj.school_name;
              existingEntry = { schoolId: id, seed, name: schoolName };
              console.log(`[Seed Logic] Created seed for search-limited user ${user.uid}: ${seed}`);
            } else {
              // Only create new entry and increment searches if under limit
              seed = Math.random().toString(36).substring(2, 15);
              const schoolName = schoolObj.school_name; // Get the school name
              existingEntry = { schoolId: id, seed, name: schoolName };
              myColleges.push(existingEntry);

              batch.update(userRef, { myColleges });
              batch.update(userRef, { searches: increment(1) }); // Increment searches
              await batch.commit();

              console.log(`[Seed Logic] Created new seed for user ${user.uid}: ${seed}`);
            }
          } else {
            // Use the existing seed
            seed = existingEntry.seed;
            console.log(`[Seed Logic] Using existing seed for user ${user.uid}: ${seed}`);
          }
          setUserSchoolSeed(seed);

          // Sort offers with this seed
          if (schoolObj.offers && schoolObj.offers.length > 0) {
            console.log(`[Seed Logic] Offer count before filtering: ${schoolObj.offers.length}`);
            
            // Filter to only show approved offers (status === 'approved' or no status field)
            const approvedOffers = schoolObj.offers.filter(offer => 
              !offer.status || offer.status === 'approved'
            );
            console.log(`[Seed Logic] Approved offer count: ${approvedOffers.length}`);
            
            const sortedOffers = [...approvedOffers];

            // [DEBUG] Show the first 2 offers before sorting
            console.log("[Seed Logic] First 2 approved offers BEFORE sorting:", sortedOffers.slice(0, 2));

            // Sort: viewed offers first, then by seeded hash
            sortedOffers.sort((a, b) => {
              const oidA = a.offerId || a.offer_id || "";
              const oidB = b.offerId || b.offer_id || "";
              const viewedA = viewedOffers[oidA];
              const viewedB = viewedOffers[oidB];
              if (viewedA && !viewedB) return -1;
              if (!viewedA && viewedB) return 1;
              const hashA = seededHash(String(a.uid ?? oidA ?? ""), seed);
              const hashB = seededHash(String(b.uid ?? oidB ?? ""), seed);
              return hashA - hashB;
            });

            console.log("[Seed Logic] Approved offers sorted (viewed first, then by seed).");
            console.log("[Seed Logic] First 2 approved offers AFTER sorting:", sortedOffers.slice(0, 2));
            const finalOrder = sortedOffers.map((o) => o.offerId ?? o.offer_id ?? o.uid ?? "unknown");
            console.log("[Seed Logic] Final approved offer ordering:", finalOrder);

            schoolObj.offers = sortedOffers;
          }
        } else {
          // If user is NOT logged in:
          console.log("[Seed Logic] No user logged in => using guest seed");
          const guestSeed = Math.random().toString(36).substring(2, 15);
          setUserSchoolSeed(guestSeed);
          console.log(`[Seed Logic] Guest seed: ${guestSeed}`);

          if (schoolObj.offers && schoolObj.offers.length > 0) {
            console.log(`[Seed Logic] Offer count before filtering: ${schoolObj.offers.length}`);
            
            // Filter to only show approved offers (status === 'approved' or no status field)
            const approvedOffers = schoolObj.offers.filter(offer => 
              !offer.status || offer.status === 'approved'
            );
            console.log(`[Seed Logic] Approved offer count: ${approvedOffers.length}`);
            
            const sortedOffers = [...approvedOffers];

            console.log("[Seed Logic] First 2 approved offers BEFORE sorting:", sortedOffers.slice(0, 2));

            sortedOffers.sort((a, b) => {
              const hashA = seededHash(a.uid || a.id || "", guestSeed);
              const hashB = seededHash(b.uid || b.id || "", guestSeed);
              return hashA - hashB;
            });

            console.log("[Seed Logic] Approved offers sorted with guest seed.");
            console.log("[Seed Logic] First 2 approved offers AFTER sorting:", sortedOffers.slice(0, 2));
            const finalOrder = sortedOffers.map((offr) => offr.uid || offr.id || "unknown");
            console.log("[Seed Logic] Final approved offer ordering:", finalOrder);

            schoolObj.offers = sortedOffers;
          }
        }

        // Extract top-level SAI if present
        if (schoolObj.efc_or_sai) {
          console.log(`[SAI] Using top-level efc_or_sai: ${schoolObj.efc_or_sai}`);
          setSAI(schoolObj.efc_or_sai);
        } else if (schoolObj.efc_brackets) {
          console.log("[SAI] Found efc_brackets, searching for true bracket...");
          let foundSAI = false;
          for (const [bracket, value] of Object.entries(schoolObj.efc_brackets)) {
            if (value === true) {
              if (bracket.startsWith("above_")) {
                const min = parseInt(bracket.replace(/[^0-9]/g, ""), 10);
                setSAI(min + 10000);
              } else {
                const [min, max] = bracket
                  .split("_")
                  .map((v) => parseInt(v.replace(/[^0-9]/g, ""), 10));
                setSAI((min + max) / 2);
              }
              foundSAI = true;
              break;
            }
          }
          if (!foundSAI) {
            console.error("[SAI] No true value found in efc_brackets.");
          }
        }

        setSchoolData(schoolObj);
      } catch (error) {
        console.error("Error fetching school data or user seed:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchSchoolAndSeed();
  }, [id, db, user]);

  useEffect(() => {
    setHasInitializedFirstPage(false);
    setTotalCreditsUsedThisSession(0);
  }, [id]);

  // ----------------------------------
  // 3) Handle Credit Deduction for Viewing Offers
  // ----------------------------------
  useEffect(() => {
    if (!loading && user && schoolData && schoolData.offers) {
      checkSchoolViewStatus();
    }
  }, [loading, user, schoolData]);

  // Count NEW offers on a page (offers user hasn't paid to view)
  const getNewOffersOnPage = (pageNum, viewed) => {
    if (!schoolData?.offers) return [];
    const start = (pageNum - 1) * ITEMS_PER_PAGE;
    const offers = schoolData.offers.slice(start, start + ITEMS_PER_PAGE);
    return offers.filter(o => {
      const oid = o.offerId || o.offer_id;
      return oid && !viewed[oid];
    });
  };

  // User has free access if they contributed an approved offer to this school (no credits, no credit to owners)
  const hasFreeAccessToThisSchool = !!(schoolData?.offers?.length && user && schoolData.offers.some(o => (o.uid || o.userId) === user.uid));

  const checkSchoolViewStatus = async () => {
    if (!schoolData || !schoolData.offers || !user || hasInitializedFirstPage) return;

    const userRef = doc(db, "users", user.uid);
    const userDoc = await getDoc(userRef);
    const freshUserData = userDoc.data() || {};
    const freshCredits = freshUserData?.searchCredit || 0;
    const viewedOffers = freshUserData?.viewedOffers || {};
    setViewedOfferIds(viewedOffers);
    setUserCredits(freshCredits);
    setHasInitializedFirstPage(true);

    setCurrentPage(1);
    handlePageNavigation(1);
  };

  const handlePageNavigation = async (newPage) => {
    if (!user) {
      setCurrentPage(newPage);
      return;
    }

    // Free access: user contributed an approved offer to this school - no credits, no credit to owners
    const hasFreeAccess = !!(schoolData?.offers?.length && schoolData.offers.some(o => (o.uid || o.userId) === user.uid));
    if (hasFreeAccess) {
      setCurrentPage(newPage);
      return;
    }

    // Prevent concurrent navigation (avoids double-charging when user clicks quickly)
    if (isNavigatingRef.current) return;
    isNavigatingRef.current = true;

    try {
      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);
      const viewedOffers = userDoc.data()?.viewedOffers || {};
      const offersOnPage = schoolData.offers.slice(
        (newPage - 1) * ITEMS_PER_PAGE,
        newPage * ITEMS_PER_PAGE
      );
      const newOffers = offersOnPage.filter(o => {
        const oid = o.offerId || o.offer_id;
        return oid && !viewedOffers[oid];
      });

      setCurrentPage(newPage);

      if (newOffers.length === 0) {
        return;
      }

      // Cap at 5 credits per page (max offers per page)
      const creditsNeeded = Math.min(ITEMS_PER_PAGE, newOffers.length);
      if ((userDoc.data()?.searchCredit ?? 0) < creditsNeeded) {
        return;
      }

      const updates = {};
      newOffers.forEach(o => {
        const oid = o.offerId || o.offer_id;
        if (oid) updates[oid] = true;
      });

      const result = await runTransaction(db, async (tx) => {
        const userSnap = await tx.get(userRef);
        const userData = userSnap.data() || {};
        const currentViewed = userData.viewedOffers || {};
        const stillNew = newOffers.filter(o => {
          const oid = o.offerId || o.offer_id;
          return oid && !currentViewed[oid];
        });
        if (stillNew.length === 0) return { success: true, free: true };
        // Cap at 5 credits per page
        const cost = Math.min(ITEMS_PER_PAGE, stillNew.length);
        const offersToCharge = stillNew.slice(0, cost);
        const balance = userData.searchCredit ?? 0;
        if (balance < cost) return { success: false, insufficient: true, have: balance };

        const newBalance = balance - cost;
        const updateData = { searchCredit: newBalance };
        offersToCharge.forEach(o => {
          const oid = o.offerId || o.offer_id;
          if (oid) updateData[`viewedOffers.${oid}`] = true;
        });
        updateData[`viewedSchools.${id}`] = true;

        tx.update(userRef, updateData);
        return { success: true, free: false, newBalance, creditedOffers: offersToCharge };
      });

      if (result.free) return;
      if (result.insufficient) return;

      setUserCredits(result.newBalance);
      setTotalCreditsUsedThisSession(prev => prev + result.creditedOffers.length);
      setViewedOfferIds(prev => ({
        ...prev,
        ...result.creditedOffers.reduce((acc, o) => {
          const oid = o.offerId || o.offer_id;
          if (oid) acc[oid] = true;
          return acc;
        }, {})
      }));

      const toCredit = result.creditedOffers
        .filter(o => (o.uid || o.userId) && (o.uid || o.userId) !== user.uid)
        .map(o => ({ offerId: o.offerId || o.offer_id, ownerUid: o.uid || o.userId }));
      if (toCredit.length > 0) {
        await creditOfferOwnersViaAPI(user.uid, toCredit);
      }
    } catch (error) {
      console.error("Error deducting credits:", error);
    } finally {
      isNavigatingRef.current = false;
    }
  };

  // ----------------------------------
  // Helper: Convert an offer to an SAI
  // ----------------------------------
  function getSAIForOffer(offer) {
    if (offer.efc_or_sai) {
      return offer.efc_or_sai;
    }

    if (offer.efc_brackets) {
      for (const [bracketKey, value] of Object.entries(offer.efc_brackets)) {
        if (value === true) {
          if (bracketKey.startsWith("above_")) {
            const min = parseInt(bracketKey.replace(/[^0-9]/g, ""), 10);
            if (!isNaN(min)) {
              return min + 10000;
            }
          } else if (bracketKey.startsWith("under_")) {
            const max = parseInt(bracketKey.replace(/[^0-9]/g, ""), 10);
            if (!isNaN(max)) {
              return max / 2;
            }
          } else if (bracketKey.includes("_")) {
            const [minStr, maxStr] = bracketKey.split("_");
            const min = parseInt(minStr, 10);
            const max = parseInt(maxStr, 10);
            if (!isNaN(min) && !isNaN(max)) {
              return (min + max) / 2;
            }
          }
        }
      }
    }

    return "N/A";
  }

  // ----------------------------------
  // Helper: Deterministic "random" sort
  // ----------------------------------
  function seededHash(offerId, seed) {
    const hash = md5(offerId + seed);
    return parseInt(hash.slice(0, 8), 16);
  }

  // ----------------------------------
  // Pagination Logic
  // ----------------------------------
  const totalOffers = schoolData?.offers?.length || 0;
  const totalPages = Math.ceil(totalOffers / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentOffers = schoolData?.offers?.slice(startIndex, startIndex + ITEMS_PER_PAGE) || [];
  const newOffersOnCurrentPage = getNewOffersOnPage(currentPage, viewedOfferIds);
  const creditsForCurrentPage = newOffersOnCurrentPage.length;
  const allViewedOnPage = currentOffers.every((o, i) => {
    const oid = getOfferId(o, id, startIndex + i);
    return oid && viewedOfferIds[oid];
  });

  // ----------------------------------
  // Rendering
  // ----------------------------------
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ fontFamily: "'DM Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif" }}>
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!schoolData || !schoolData.offers || totalOffers === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ fontFamily: "'DM Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif" }}>
        <p className="text-gray-500">No offers found for the selected school.</p>
      </div>
    );
  }

  const formatNumber = (value, noComma = false) => {
    if (!value) return "0";
    const num = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(num)) return "0";
    return noComma ? num.toFixed(0) : num.toLocaleString("en-US", { maximumFractionDigits: 0 });
  };

  const capitalizeFirstLetter = (name) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((word) => {
        if (word.length === 0) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(" ");
  };

  // Helper function to check if an offer has any details
  const hasOfferDetails = (offer) => {
    const detailFields = [
      'cost_of_attendance_details',
      'financial_aid_details', 
      'merit_aid_details',
      'other_aid_details',
      'federal_money_details',
      'workstudy_details',
      'loans_details',
      'efc_or_sai_details'
    ];
    
    return detailFields.some(field => offer[field] && offer[field].trim() !== '');
  };

  // Helper function to toggle offer expansion
  const toggleOfferExpansion = (offerIndex) => {
    setExpandedOffer(expandedOffer === offerIndex ? null : offerIndex);
  };

  const card = { background: "#fff", border: "1px solid #e4e7f0", borderRadius: 14, overflow: "hidden", fontFamily: "'DM Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif" };
  const divider = { height: 1, background: "#e4e7f0" };
  const th = { padding: "9px 12px", fontSize: 10, fontWeight: 700, color: "#9ca3b4", letterSpacing: ".07em", textTransform: "uppercase", textAlign: "left", whiteSpace: "nowrap", borderBottom: "1px solid #e4e7f0" };
  const td = { padding: "11px 12px", fontSize: 13, color: "#4b5568", borderBottom: "1px solid #e4e7f0", whiteSpace: "nowrap" };
  const fmt = (n) => (n != null ? "$" + Number(n).toLocaleString() : "—");

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white text-gray-900" style={{ fontFamily: "'DM Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif" }}>
      
      <div className="min-h-screen flex flex-col items-center justify-center p-6">
        <h1 className="text-3xl font-extrabold mb-4">
          {capitalizeFirstLetter(schoolData.school_name)} Offers
        </h1>

        {/* Credit Information */}
        {user && (
          <div className="mb-6 max-w-4xl w-full">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700">
                      Credits Available: <span className="text-blue-600 font-bold">{userCredits}</span>
                    </span>
                  </div>
                  {totalCreditsUsedThisSession > 0 && (
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700">
                        Credits Used This Session: <span className="text-green-600 font-bold">{totalCreditsUsedThisSession}</span>
                      </span>
                    </div>
                  )}
                  {hasFreeAccessToThisSchool && (
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700">
                        <span className="text-green-600 font-bold">Free access — you contributed an approved offer</span>
                      </span>
                    </div>
                  )}
                  {!hasFreeAccessToThisSchool && allViewedOnPage && (
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700">
                        <span className="text-blue-600 font-bold">All offers on page already viewed</span>
                      </span>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages} ({currentOffers.length} offers on this page)
                  </div>
                  <div className="text-sm text-gray-600">
                    Total: {totalOffers} offers available
                  </div>
                  {hasFreeAccessToThisSchool ? (
                    <div className="text-xs text-green-600 font-medium">✓ Free access</div>
                  ) : allViewedOnPage ? (
                    <div className="text-xs text-green-600 font-medium">✓ All offers viewed</div>
                  ) : (
                    <div className="text-xs text-blue-600 font-medium">
                      {creditsForCurrentPage} credits for {creditsForCurrentPage} new offer{creditsForCurrentPage !== 1 ? "s" : ""}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}



        {/* Table Container - Profile style */}
        <div style={{ ...card, width: "100%", maxWidth: 1100 }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 780 }}>
              <caption style={{ padding: "12px 16px", fontSize: 12, color: "#9ca3b4", textAlign: "left" }}>
                Financial and Academic Offers for {capitalizeFirstLetter(schoolData.school_name)}
              </caption>
              <thead style={{ background: "#f0f2f8" }}>
                <tr>
                  {["ID", "Status", "School", "Cost", "Financial Aid", "Merit Aid", "Other Aid", "Test", "GPA", "SAI", "State", "Admission"].map((h) => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {user && (hasFreeAccessToThisSchool || allViewedOnPage || userCredits >= creditsForCurrentPage) ? (
                  currentOffers.map((offer, index) => (
                    <React.Fragment key={index}>
                      <tr
                        style={{
                          background: index % 2 === 0 ? "#fff" : "#f9fafb",
                          cursor: hasOfferDetails(offer) ? "pointer" : "default",
                        }}
                        onClick={() => hasOfferDetails(offer) && toggleOfferExpansion(index)}
                      >
                        <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: 11, color: "#9ca3b4" }}>
                          {(offer.offerId || offer.offer_id || "").toString().slice(0, 4) || "—"}
                        </td>
                        <td style={td}>
                          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: "rgba(22,163,74,.1)", color: "#16a34a" }}>
                              <span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor", display: "inline-block" }} />verified
                            </span>
                          </div>
                        </td>
                        <td style={{ ...td, fontWeight: 500, color: "#111827" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            {capitalizeFirstLetter(schoolData.school_name)}
                            {hasOfferDetails(offer) && (
                              <svg style={{ width: 14, height: 14, transform: expandedOffer === index ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </td>
                        <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: 12 }}>{fmt(offer.cost_of_attendance)}</td>
                        <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: 12 }}>{fmt((offer.financial_aid || 0) + (offer.need_based_grant_aid || 0))}</td>
                        <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: 12 }}>{fmt(offer.merit_aid)}</td>
                        <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: 12 }}>{fmt(offer.other_aid || offer.other_private_scholarships)}</td>
                        <td style={td}>
                          {offer.composite_sat ? `SAT: ${formatNumber(offer.composite_sat, true)}` : offer.composite_act ? `ACT: ${formatNumber(offer.composite_act, true)}` : "N/A"}
                        </td>
                        <td style={td}>{offer.gpa || "N/A"}</td>
                        <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: 12 }}>
                          {(() => {
                            const saiValue = getSAIForOffer(offer);
                            return saiValue != null ? formatNumber(saiValue) : "N/A";
                          })()}
                        </td>
                        <td style={td}>{offer.state_of_residence || "N/A"}</td>
                        <td style={td}>{offer.admission_type || "Regular Admission"}</td>
                      </tr>
                      {expandedOffer === index && hasOfferDetails(offer) && (
                        <tr>
                          <td colSpan={12} style={{ padding: 0, borderBottom: "1px solid #e4e7f0", verticalAlign: "top" }}>
                            <div style={{ background: "#f9fafb", borderTop: "1px solid #e4e7f0", padding: 16 }}>
                              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
                                {offer.cost_of_attendance_details && (
                                  <div style={{ background: "#fff", padding: 12, borderRadius: 8, border: "1px solid #e4e7f0" }}>
                                    <h4 style={{ fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 6 }}>Cost of Attendance</h4>
                                    <p style={{ fontSize: 13, color: "#4b5568", lineHeight: 1.5 }}>{offer.cost_of_attendance_details}</p>
                                  </div>
                                )}
                                {offer.financial_aid_details && (
                                  <div style={{ background: "#fff", padding: 12, borderRadius: 8, border: "1px solid #e4e7f0" }}>
                                    <h4 style={{ fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 6 }}>Financial Aid</h4>
                                    <p style={{ fontSize: 13, color: "#4b5568", lineHeight: 1.5 }}>{offer.financial_aid_details}</p>
                                  </div>
                                )}
                                {offer.merit_aid_details && (
                                  <div style={{ background: "#fff", padding: 12, borderRadius: 8, border: "1px solid #e4e7f0" }}>
                                    <h4 style={{ fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 6 }}>Merit Aid</h4>
                                    <p style={{ fontSize: 13, color: "#4b5568", lineHeight: 1.5 }}>{offer.merit_aid_details}</p>
                                  </div>
                                )}
                                {offer.other_aid_details && (
                                  <div style={{ background: "#fff", padding: 12, borderRadius: 8, border: "1px solid #e4e7f0" }}>
                                    <h4 style={{ fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 6 }}>Other Aid</h4>
                                    <p style={{ fontSize: 13, color: "#4b5568", lineHeight: 1.5 }}>{offer.other_aid_details}</p>
                                  </div>
                                )}
                                {offer.federal_money_details && (
                                  <div style={{ background: "#fff", padding: 12, borderRadius: 8, border: "1px solid #e4e7f0" }}>
                                    <h4 style={{ fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 6 }}>Federal Money</h4>
                                    <p style={{ fontSize: 13, color: "#4b5568", lineHeight: 1.5 }}>{offer.federal_money_details}</p>
                                  </div>
                                )}
                                {offer.workstudy_details && (
                                  <div style={{ background: "#fff", padding: 12, borderRadius: 8, border: "1px solid #e4e7f0" }}>
                                    <h4 style={{ fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 6 }}>Work Study</h4>
                                    <p style={{ fontSize: 13, color: "#4b5568", lineHeight: 1.5 }}>{offer.workstudy_details}</p>
                                  </div>
                                )}
                                {offer.loans_details && (
                                  <div style={{ background: "#fff", padding: 12, borderRadius: 8, border: "1px solid #e4e7f0" }}>
                                    <h4 style={{ fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 6 }}>Loans</h4>
                                    <p style={{ fontSize: 13, color: "#4b5568", lineHeight: 1.5 }}>{offer.loans_details}</p>
                                  </div>
                                )}
                                {offer.efc_or_sai_details && (
                                  <div style={{ background: "#fff", padding: 12, borderRadius: 8, border: "1px solid #e4e7f0" }}>
                                    <h4 style={{ fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 6 }}>EFC/SAI</h4>
                                    <p style={{ fontSize: 13, color: "#4b5568", lineHeight: 1.5 }}>{offer.efc_or_sai_details}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan={12} style={{ ...td, textAlign: "center", color: "#9ca3b4", fontStyle: "italic" }}>
                      {!user ? "Log in to view offers" : `You need ${creditsForCurrentPage} credits for ${creditsForCurrentPage} new offer(s). You have ${userCredits} credits.`}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination - Profile style */}
          {totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "#f0f2f8", borderTop: "1px solid #e4e7f0" }}>
              <span style={{ fontSize: 12, color: "#9ca3b4" }}>
                Showing {startIndex + 1}–{Math.min(startIndex + ITEMS_PER_PAGE, totalOffers)} of {totalOffers}
              </span>
              <div style={{ display: "flex", gap: 4 }}>
                <button
                  disabled={currentPage === 1}
                  onClick={() => { const newPage = Math.max(currentPage - 1, 1); if (newPage !== currentPage) handlePageNavigation(newPage); }}
                  style={{ minWidth: 30, height: 30, borderRadius: 6, border: "1px solid #e4e7f0", background: "#fff", fontSize: 12, fontWeight: 600, color: "#4b5568", cursor: "pointer", opacity: currentPage === 1 ? 0.3 : 1, fontFamily: "inherit" }}
                >←</button>
                {Array.from({ length: totalPages }, (_, i) => {
                  const pageNumber = i + 1;
                  const newOnPage = getNewOffersOnPage(pageNumber, viewedOfferIds).length;
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => { if (pageNumber !== currentPage) handlePageNavigation(pageNumber); }}
                      style={{
                        minWidth: 30, height: 30, borderRadius: 6, border: "1px solid #e4e7f0",
                        background: currentPage === pageNumber ? "#4361ee" : "#fff",
                        color: currentPage === pageNumber ? "#fff" : "#4b5568",
                        fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit"
                      }}
                    >
                      {pageNumber}
                      {!hasFreeAccessToThisSchool && newOnPage > 0 && pageNumber !== currentPage && <span style={{ marginLeft: 2, fontSize: 10, color: "#d97706" }}>({newOnPage}C)</span>}
                    </button>
                  );
                })}
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => { const newPage = Math.min(currentPage + 1, totalPages); if (newPage !== currentPage) handlePageNavigation(newPage); }}
                  style={{ minWidth: 30, height: 30, borderRadius: 6, border: "1px solid #e4e7f0", background: "#fff", fontSize: 12, fontWeight: 600, color: "#4b5568", cursor: "pointer", opacity: currentPage === totalPages ? 0.3 : 1, fontFamily: "inherit" }}
                >→</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
